import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  EmptyState,
  ErrorState,
  Separator,
  Skeleton,
} from '@vta/ui';
import type { Program, TeamSummary } from '@vta/types';
import { apiClient, ApiError } from '../../lib/api-client.ts';
import { useSelectedTeamStore } from '../../stores/selected-team-store.ts';
import { CityTeamList } from './CityTeamList.tsx';
import { ProgramBreakdownBar } from './ProgramBreakdownBar.tsx';

interface CityResponse {
  city: {
    citySlug?: string;
    city?: string | null;
    region?: string | null;
    country?: string | null;
    countrySlug?: string | null;
    teamCount?: number;
    programBreakdown?: Partial<Record<Program, number>> | null;
  };
  teams: TeamSummary[];
}

/**
 * Drawer body shown when a city cluster on the globe is selected.
 *
 * The cluster handler in GlobeScene stores the city's `slug`, `city`, and
 * `country` in the selected-team store; this component fetches the rest from
 * `/api/cities/:slug` and renders a compact view: city kicker, team count,
 * program breakdown bar, full team list, and a CTA to open the standalone
 * /city/:slug page.
 */
export function CityDrawerBody({
  citySlug,
  fallbackCity,
  fallbackCountry,
}: {
  citySlug: string;
  fallbackCity?: string | null;
  fallbackCountry?: string | null;
}) {
  const navigate = useNavigate();
  const close = useSelectedTeamStore((s) => s.close);

  const query = useQuery<CityResponse | null>({
    queryKey: ['city', citySlug],
    queryFn: ({ signal }) =>
      apiClient
        .get<CityResponse>(`/api/cities/${encodeURIComponent(citySlug)}`, { signal })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
  });

  const data = query.data;
  const heading = data?.city.city ?? fallbackCity ?? citySlug;
  const sub =
    data?.city.country ??
    fallbackCountry ??
    (data?.city.region ?? null);

  const breakdown = data?.city.programBreakdown ?? deriveBreakdown(data?.teams ?? []);
  const total =
    data?.city.teamCount ??
    data?.teams.length ??
    0;

  return (
    <>
      <DrawerHeader>
        <p className="text-2xs uppercase tracking-[0.3em] text-fg-subtle">City</p>
        <h2 className="font-display text-xl tracking-tight">{heading}</h2>
        {sub ? <p className="text-sm text-fg-muted">{sub}</p> : null}
      </DrawerHeader>

      <DrawerBody>
        {query.isLoading ? (
          <DrawerLoading />
        ) : query.isError ? (
          <ErrorState
            title="Could not load this city"
            description="Network or server hiccup. Try again."
            retry={() => query.refetch()}
          />
        ) : !data ? (
          <EmptyState
            title="City not found"
            description="This cluster may be a synthetic super-cluster spanning multiple cities. Zoom in to see individual cities."
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm text-fg-muted">
                <span className="font-mono text-fg">{total}</span>{' '}
                {total === 1 ? 'team' : 'teams'}
              </p>
              <Badge variant="neutral">Cluster</Badge>
            </div>

            <ProgramBreakdownBar breakdown={breakdown} />

            <Separator />

            <CityTeamList teams={data.teams} />
          </div>
        )}
      </DrawerBody>

      <DrawerFooter>
        <div className="flex items-center justify-between gap-2">
          {data ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                close();
                navigate({ to: '/city/$citySlug', params: { citySlug } });
              }}
            >
              Open full page
            </Button>
          ) : (
            <span />
          )}
          <Button variant="ghost" size="sm" onClick={close}>
            Close
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
}

function deriveBreakdown(teams: TeamSummary[]): Partial<Record<Program, number>> {
  const out: Partial<Record<Program, number>> = {};
  for (const t of teams) out[t.program] = (out[t.program] ?? 0) + 1;
  return out;
}

function DrawerLoading() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}
